import { fetchAuthSession } from "@aws-amplify/auth";
import type {
  ApiResponse,
  CreateUserRequest,
  CreateUserResponse,
  DiagnosticConfig,
  DiagnosticLog,
  DiagnosticLogFilters,
  DiagnosticLogsResponse,
  ResetPasswordResponse,
  SensorFilters,
  ParticipantSurveySchedule,
  ParticipantSurveyScheduleUpdate,
  SurveyConfig,
  SurveyConfigUpdate,
} from "./types";

const BASE_URL = import.meta.env.VITE_API_URL;

async function getAuthHeaders(): Promise<HeadersInit> {
  const session = await fetchAuthSession();
  const token = session.tokens?.accessToken?.toString();
  if (!token) throw new Error("Not authenticated");
  return { Authorization: `Bearer ${token}` };
}

export async function fetchApi<T>(
  path: string,
  params?: Record<string, string | undefined>
): Promise<ApiResponse<T>> {
  const url = new URL(`${BASE_URL}${path}`);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") {
        url.searchParams.set(key, value);
      }
    }
  }

  const headers = await getAuthHeaders();
  const response = await fetch(url.toString(), { headers });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  return response.json() as Promise<ApiResponse<T>>;
}

export async function fetchPublicApi<T>(path: string): Promise<ApiResponse<T>> {
  const response = await fetch(new URL(`${BASE_URL}${path}`).toString());

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  return response.json() as Promise<ApiResponse<T>>;
}

export async function resetUserPassword(username: string): Promise<ResetPasswordResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${BASE_URL}/admin/users/${encodeURIComponent(username)}/reset-password`, {
    method: "POST",
    headers,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `API error: ${response.status}`);
  }

  return response.json() as Promise<ResetPasswordResponse>;
}

export async function deleteUser(username: string): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${BASE_URL}/admin/users/${encodeURIComponent(username)}`, {
    method: "DELETE",
    headers,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `API error: ${response.status}`);
  }
}

export async function createUser(body?: CreateUserRequest): Promise<CreateUserResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${BASE_URL}/users`, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `API error: ${response.status}`);
  }

  return response.json() as Promise<CreateUserResponse>;
}

export async function putApi<T>(path: string, body: unknown): Promise<T> {
  const url = new URL(`${BASE_URL}${path}`);
  const headers = await getAuthHeaders();
  const response = await fetch(url.toString(), {
    method: "PUT",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || `API error: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

export async function fetchSurveyConfig(): Promise<ApiResponse<SurveyConfig>> {
  return fetchApi<SurveyConfig>("/admin/surveys/config");
}

export async function updateSurveyConfig(config: SurveyConfigUpdate): Promise<ApiResponse<SurveyConfig>> {
  return putApi<ApiResponse<SurveyConfig>>("/admin/surveys/config", config);
}

export async function fetchParticipantSurveySchedule(userId: string): Promise<ApiResponse<ParticipantSurveySchedule>> {
  return fetchApi<ParticipantSurveySchedule>(`/admin/participants/${encodeURIComponent(userId)}/survey-schedule`);
}

export async function updateParticipantSurveySchedule(
  userId: string,
  schedule: ParticipantSurveyScheduleUpdate,
): Promise<ApiResponse<ParticipantSurveySchedule>> {
  return putApi<ApiResponse<ParticipantSurveySchedule>>(
    `/admin/participants/${encodeURIComponent(userId)}/survey-schedule`,
    schedule,
  );
}

export async function downloadExport(
  params?: SensorFilters & { format?: string }
): Promise<void> {
  return downloadFile("/sensors/export", params, `export.${params?.format ?? "csv"}`);
}

async function downloadFile(
  path: string,
  params: object | undefined,
  fallbackFilename: string,
): Promise<void> {
  const url = new URL(`${BASE_URL}${path}`);

  if (params) {
    for (const [key, value] of Object.entries(params as Record<string, string | number | undefined>)) {
      if (value !== undefined && value !== "") {
        url.searchParams.set(key, String(value));
      }
    }
  }

  const headers = await getAuthHeaders();
  const response = await fetch(url.toString(), { headers });

  if (!response.ok) {
    throw new Error(`Export failed: ${response.status} ${response.statusText}`);
  }

  const blob = await response.blob();
  const filename = response.headers.get("content-disposition")?.match(/filename="?([^"]+)"?/)?.[1]
    ?? fallbackFilename;
  const downloadUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(downloadUrl);
}

export async function fetchParticipantDiagnosticConfig(
  userId: string,
): Promise<ApiResponse<DiagnosticConfig>> {
  return fetchApi<DiagnosticConfig>(
    `/admin/participants/${encodeURIComponent(userId)}/diagnostic-logging`,
  );
}

export async function updateParticipantDiagnosticConfig(
  userId: string,
  config: { enabled: boolean; duration_hours: 24 | 48 | 168 },
): Promise<ApiResponse<DiagnosticConfig>> {
  return putApi<ApiResponse<DiagnosticConfig>>(
    `/admin/participants/${encodeURIComponent(userId)}/diagnostic-logging`,
    config,
  );
}

export async function fetchParticipantDiagnosticLogs(
  userId: string,
  filters: DiagnosticLogFilters,
): Promise<ApiResponse<DiagnosticLog[]>> {
  const response = await fetchApi<DiagnosticLogsResponse>(
    `/admin/participants/${encodeURIComponent(userId)}/diagnostic-logs`,
    {
      from: filters.from,
      to: filters.to,
      device_id: filters.device_id,
      category: filters.category,
      level: filters.level,
      event_name: filters.event_name,
      cursor: filters.cursor,
      limit: filters.limit ? String(filters.limit) : undefined,
    },
  );
  return {
    data: response.data.items,
    count: response.data.count,
    next_cursor: response.data.next_cursor,
  };
}

export async function downloadParticipantDiagnosticLogs(
  userId: string,
  filters: Omit<DiagnosticLogFilters, "cursor" | "limit">,
  format: "json" | "csv",
): Promise<void> {
  await downloadFile(
    `/admin/participants/${encodeURIComponent(userId)}/diagnostic-logs/export`,
    {
      from: filters.from,
      to: filters.to,
      device_id: filters.device_id,
      category: filters.category,
      level: filters.level,
      event_name: filters.event_name,
      format,
    },
    `diagnostic-logs.${format}`,
  );
}
